import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { ErrorMultiWrapper } from './components/error-wrappers/ErrorMultiWrapper';
import { usePortfolioComparison } from './logic/usePortfolioComparison';
import ExportableGrid from './components/grid/ExportableGrid';
import { useIsBlockGridCompact } from './logic/useIsBlockGridCompact';
import { useCommonTreeGridProps } from './logic/columnUtils';
import { autoGroupColumnDefStaticConstants } from './components/grid/AgGridThemeOverrides';
import { useBlockId } from './contexts/BlockIdContext';
import { UnexpectedErrorWrapper } from './components/error-wrappers/UnexpectedErrorWrapper';
import { InfoGraphicTypeErrorWrapper } from './components/error-wrappers/InfoGraphicTypeErrorWrapper';
import { ComparisonMetricsErrorWrapper } from './components/error-wrappers/ComparisonMetricsErrorWrapper';
import ValueCellRenderer from './components/grid/renderers/ValueCellRenderer';
import { ComparisonSubjectsErrorWrapper } from './components/error-wrappers/ComparisonSubjectsErrorWrapper';
import { useHasFF, withSuspense } from 'venn-utils';
import { usePortfolioGridDisplaySettings } from './logic/usePortfolioGridDisplaySettings';
import { useLabelColumnWidth } from './logic/useLabelColumnMaxWidth';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { AllocationRowData } from './logic/usePortfolioComparisonUtils';
import { BlockSuspenseFallback } from './components/BlockSuspenseFallback';
import { ComparisonDuplicateSubjectsError } from './components/error-wrappers/ComparisonDuplicateSubjectsError';
import { getDefaultValueFormat } from './logic/gridStyling';
import type { CellClassParams } from '../data-grid';
import { BOLD_CLASS } from './customAnalysisContants';
import { NoStrategyLevelsSelectedErrorWrapper } from './components/error-wrappers/NoStrategyLevelsSelectedErrorWrapper';
import { flattenedProxyMapState, vdparFundIdsState } from 'venn-state';
import {
  renderSuperscripts,
  usePageProxySymbol,
  usePageVdparSymbol,
} from '../blocks/components/core/block-subject-footnotes-and-superscripts';
import { OptionIcon } from '../common/OptionIcon';

const useAutoGroupColumnDef = (columnDefs: ColGroupDef<AllocationRowData>[]): ColDef => {
  const { maxWidth, minWidth } = useLabelColumnWidth(columnDefs);
  const proxyMap = useRecoilValue(flattenedProxyMapState);
  const proxySymbol = usePageProxySymbol();
  const vdparFundIds = useRecoilValue(vdparFundIdsState);
  const vdparSymbol = usePageVdparSymbol();

  return useMemo(
    () => ({
      ...autoGroupColumnDefStaticConstants,
      autoHeight: true,
      minWidth,
      maxWidth,
      cellRendererParams: {
        ...autoGroupColumnDefStaticConstants.cellRendererParams,
        innerRendererParams: { editable: true },
      },
      cellRenderer: ({ data }: { data: AllocationRowData }) => {
        if (!data.fundId && !data.optionData) {
          return data.label;
        }
        const icon = data.optionData ? <OptionIcon optionType={data.optionData.optionType} /> : null;
        return (
          <>
            {data.label}
            {icon && <> {icon}</>}
            {renderSuperscripts([
              { condition: !!data.fundId && !!proxyMap?.[data.fundId], symbol: proxySymbol },
              { condition: !!data.fundId && vdparFundIds.has(data.fundId), symbol: vdparSymbol },
            ])}
          </>
        );
      },
    }),
    [maxWidth, minWidth, proxyMap, proxySymbol, vdparFundIds, vdparSymbol],
  );
};

const treeGridOverrides: ColDef<AllocationRowData> = {
  cellRenderer: ValueCellRenderer,
  valueFormatter: getDefaultValueFormat,
  cellClassRules: {
    [BOLD_CLASS]: (params: CellClassParams<AllocationRowData>) => !!params.data?.isStrategy,
  },
};

const PortfolioComparisonBlockInner = () => {
  const blockId = useBlockId();
  const isCompact = useIsBlockGridCompact();
  const skipKSuffixInFormatting = useHasFF('skip_k_suffix_in_formatting_ff');

  const extraDefaultColDef = {
    ...treeGridOverrides,
    ...{ cellRendererParams: { skipKSuffixInFormatting } },
  };
  const commonGridProps = useCommonTreeGridProps(extraDefaultColDef);

  const { rowData, columnDefs, onCellValueChanged, pinnedBottomRowData } = usePortfolioComparison(blockId);
  const shouldExpand = usePortfolioGridDisplaySettings(rowData, columnDefs);
  const autoGroupColumnDef = useAutoGroupColumnDef(columnDefs);

  return (
    <ExportableGrid
      {...commonGridProps}
      exportable
      autoGroupColumnDef={autoGroupColumnDef}
      selectedRefId={blockId}
      isCompact={isCompact}
      rowData={rowData}
      columnDefs={columnDefs}
      shouldExpand={shouldExpand}
      onCellValueChanged={onCellValueChanged}
      pinnedBottomRowData={pinnedBottomRowData}
      treeData
    />
  );
};

const PurePortfolioComparisonBlock = () => {
  const blockId = useBlockId();

  return (
    <ErrorMultiWrapper
      wrappers={[
        UnexpectedErrorWrapper,
        InfoGraphicTypeErrorWrapper,
        ComparisonSubjectsErrorWrapper,
        ComparisonMetricsErrorWrapper,
        ComparisonDuplicateSubjectsError,
        NoStrategyLevelsSelectedErrorWrapper,
      ]}
      blockId={blockId}
    >
      <PortfolioComparisonBlockInner />
    </ErrorMultiWrapper>
  );
};

export const DeprecatedPortfolioComparisonBlock = React.memo(
  withSuspense(<BlockSuspenseFallback />, PurePortfolioComparisonBlock),
);
